import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { Grain, Thoranam } from '../components/Thoranam';
import { NavBar } from '../components/NavBar';
import { seedCatalog } from '../lib/album';
import { authMessage } from '../lib/authErrors';
import type { AllowlistEntry, Role } from '../types';

export function AdminPage() {
  const auth = useAuth();
  const [rows, setRows] = useState<AllowlistEntry[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('guest');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setRows(await auth.listAllowlist());
  }

  useEffect(() => {
    void refresh().catch((e: unknown) => setErr(authMessage(e)));
    // listAllowlist is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.listAllowlist]);

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await auth.addInvite(email, role);
      setEmail('');
      setMsg(`Invited ${email}`);
      await refresh();
    } catch (e) {
      setErr(authMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function onSeed() {
    setBusy(true);
    setErr(null);
    try {
      const n = await seedCatalog();
      setMsg(n === 0 ? 'Catalogue already present.' : `Inscribed ${n} photographs.`);
    } catch (e) {
      setErr(authMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="shell">
      <div className="bg bg-admin" />
      <Thoranam />
      <Grain />
      <NavBar />
      <main className="page">
        <header className="page-head">
          <p className="kicker">Household</p>
          <h1>Google whitelist</h1>
          <p>Only these Gmail / Google account addresses may open the album after sign-in.</p>
        </header>
        <section className="admin-card">
          <h2>Allowed Google accounts</h2>
          <p>
            Add the exact address Google uses (usually <code>name@gmail.com</code>). A person who is
            not on this list is sent to the no-access page after they sign in.
          </p>
          <form className="admin-form" onSubmit={onAdd}>
            <input
              type="email"
              required
              placeholder="name@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="guest">Guest</option>
              <option value="admin">Admin</option>
            </select>
            <button className="btn" type="submit" disabled={busy}>
              Add
            </button>
          </form>
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.email}>
                  <td>{row.email}</td>
                  <td>{row.role}</td>
                  <td>
                    <button
                      type="button"
                      className="btn ghost"
                      disabled={busy || row.email === auth.profile?.email}
                      onClick={() =>
                        void auth
                          .removeInvite(row.email)
                          .then(refresh)
                          .catch((e: unknown) => setErr(authMessage(e)))
                      }
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <hr className="gold-rule" />
          <h2>Catalogue</h2>
          <p>
            Writes 378 photo documents (filenames, order, dimensions) to Firestore. Image files stay
            off-site until you run the Storage upload script.
          </p>
          <div className="btn-row">
            <button className="btn gold" type="button" disabled={busy} onClick={() => void onSeed()}>
              Inscribe the catalogue
            </button>
          </div>
          {msg ? <p className="msg ok">{msg}</p> : null}
          {err ? <p className="msg">{err}</p> : null}
        </section>
      </main>
    </div>
  );
}
